import React, { useEffect, useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { BsPlusCircleFill } from "react-icons/bs";
import { AddUpdateDeadline } from "./AddUpdateDeadline";
import { useDeadlineColumn } from "./hooks";
import { useListApi } from "@/hooks/useListApi";
import { Deadline, deadlinesApi } from "@/api/deadlines";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { CustomPageSelector, CustomPagination } from "../CustomPagination";
import { CustomPaginationInfo } from "../CustomPaginationInfo";
import { usePermission } from "@/hooks/usePermission";
import { Case } from "@/api/cases";
import { useTranslation } from "react-i18next";

function DeadlineTableForCase({
  reviewCase,
  refreshKey,
}: {
  reviewCase: Case;
  refreshKey?: string;
}) {
  const { t } = useTranslation();
  const { loading, refresh, apiResult, search, pagination, order } =
    useListApi<Deadline>({
      baseApiObject: deadlinesApi,
      defaultParams: { case_id: reviewCase.id },
      enable: true,
    });
  const [selectedDeadline, selectDeadline] = useState<Deadline>();
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const hasAddDeadlinePermission = usePermission("review.add_deadline");

  const columns = useDeadlineColumn({
    refresh,
    edit: (item: Deadline) => {
      selectDeadline(item);
      setOpenEdit(true);
    },
    setOrder: order.set,
  });

  useEffect(() => {
    if (refreshKey) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return (
    <div className="w-full my-8">
      <div className="flex justify-between items-center">
        {hasAddDeadlinePermission && (
          <AddUpdateDeadline
            open={openCreate}
            setOpen={setOpenCreate}
            trigger={
              <Button className="flex gap-2">
                <BsPlusCircleFill /> {t("deadlines.button.add")}
              </Button>
            }
            refresh={refresh}
            defaultCase={reviewCase}
          />
        )}
        <AddUpdateDeadline
          open={openEdit}
          setOpen={setOpenEdit}
          deadline={selectedDeadline}
          refresh={refresh}
          defaultCase={reviewCase}
        />
      </div>
      <div className="flex gap-2 w-full my-4 md:w-[410px]">
        <Input
          value={search.value}
          type="text"
          placeholder={t("common.placeholder.search")}
          onChange={(event) => search.set(event.target.value)}
        />
        {search.value && (
          <Button onClick={() => search.set("")}>
            {t("common.button.clear")}
          </Button>
        )}
      </div>
      {loading ? (
        <div className="flex min-h-[300px] md:min-h-[450px] items-center justify-center max-h-auto">
          <Loader />
        </div>
      ) : (
        <>
          <div className="min-h-[300px] md:min-h-[450px] max-h-auto">
            <DataTable columns={columns} data={apiResult?.results || []} />
            <div className="flex flex-col md:flex-row md:justify-between">
              <div className="flex flex-col md:flex-row md:w-2/3   md:space-x-4 mt-3">
                <CustomPageSelector
                  pageSize={pagination.pageSize}
                  persistenceKey={`deadlinePageSize_${reviewCase.id}`}
                  persistPagination={true}
                  onPageSizeChange={(value) => {
                    pagination.setPageSize(value);
                    pagination.setPage(1);
                  }}
                />
                <CustomPaginationInfo
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                />
              </div>
              <div className="flex md:space-x-4 mt-4 pr-5 pt-2">
                <CustomPagination
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                  onPageChange={(value) => pagination.setPage(value)}
                  persistenceKey={`deadlinePage_${reviewCase.id}`}
                  persistPagination={true}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DeadlineTableForCase;
